import {
  createStyles,
  makeStyles,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import { Add } from '@mui/icons-material'
import React, { useState } from 'react'
import {
  List,
  required,
  ResourceContextProvider,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  TopToolbar,
  useTranslate,
} from 'react-admin'
import { ResourceName } from '../../../core/ResourceName'
import { useHasAuthority } from '../../hooks/useHasAuthority'
import { Authority } from '../../../core/auth/Authority'
import EditableDatagrid, {
  ActionsColumnProp,
} from '../../common/customized-ra-components/EditableDatagrid'
import { printItemParametersDto } from '../../../core/dto/print-item-parameters.dto'
import {
  DeleteRowButton,
  EditRowButton,
  InlineCreateButton,
  RowForm,
} from '../../../lib/@react-admin/ra-editable-datagrid'
import { PrintParameterTypes } from '../../../core/enum/PrintParameterTypes'
import Button from '../../common/customized-mui-components/Button'
import { CreateDialog } from '../../common/customized-ra-components/CreateDialog'

const useStyles = makeStyles(() =>
  createStyles({
    noHeight: {
      minHeight: '0px !important',
      padding: '0px !important',
      margin: '0px !important',
    },
    listMarginCorrection: {
      '& > *': {
        minHeight: '0px !important',
      },
    },
    addButton: {
      marginLeft: '30rem',
      whiteSpace: 'nowrap',
    },
  }),
)

const ActionsColumn = ({
  mutationMode,
  noDelete,
  undoable,
  ...props
}: ActionsColumnProp) => (
  <>
    <EditRowButton />
    {!noDelete && (
      <DeleteRowButton
        mutationMode={mutationMode}
        undoable={undoable}
        {...props}
      />
    )}
  </>
)

const validateItemParameters = (values: printItemParametersDto) => {
  const errors: { [n: string]: string } = {}
  if (!values.key) {
    errors.key = 'ra.validation.required'
  }
  if (!values.type) {
    errors.type = 'ra.validation.required'
  }
  return errors
}

export const printParameterTypeChoices = Object.values(PrintParameterTypes)
  .sort((a, b) => a.localeCompare(b))
  .map((value) => ({ id: value, name: value }))

const ItemParametersTopToolbarActions = ({ ...props }) => {
  const { handleOpenCreate, ...rest } = props
  const translate = useTranslate()
  const hasAuthority = useHasAuthority()
  const classes = useStyles()

  return (
    <TopToolbar {...rest} className={classes.noHeight}>
      <Typography variant="subtitle1" style={{ whiteSpace: 'nowrap' }}>
        {`${translate(
          'resources.print-item-parameters.titles.item-parameter-incline-missing',
        )}`}
      </Typography>
      <Button
        className={classes.addButton}
        label={translate('resources.print-item-parameters.actions.add')}
        onClick={handleOpenCreate}
        startIcon={<Add />}
        disabled={
          !hasAuthority(Authority.EDIT_PRINT_ITEM_PARAMETERS) ||
          !hasAuthority(Authority.CREATE_PRINT_ITEM_PARAMETERS)
        }
      />
    </TopToolbar>
  )
}

const PrintItemParametersCreateDialog = (props: any) => {
  const { itemId } = props

  return (
    <CreateDialog
      {...props}
      title="resources.print-item-parameters.titles.create"
    >
      <SimpleForm
        initialValues={{
          printItemId: itemId,
        }}
        redirect={false}
        validate={validateItemParameters}
      >
        <TextField
          label="resources.print-item-parameters.fields.printItemId"
          source="id"
        />
        <TextInput source="key" validate={required()} />
        <TextInput source="options" />
        <SelectInput
          resource={ResourceName.PRINT_ITEM_PARAMETERS}
          source="type"
          fullWidth
          choices={printParameterTypeChoices}
          validate={required()}
        />
      </SimpleForm>
    </CreateDialog>
  )
}

const ItemParameterEdit = ({ ...props }) => (
  <RowForm {...props} validate={validateItemParameters}>
    <TextField source="id" />
    <TextInput source="key" label=" " />
    <TextInput source="options" label=" " />
    <SelectInput
      resource={ResourceName.PRINT_ITEM_PARAMETERS}
      source="type"
      label=" "
      fullWidth
      choices={printParameterTypeChoices}
    />
  </RowForm>
)

export const ItemParametersGridExpand = ({ ...props }) => {
  const { id: itemId } = props
  const theme = useTheme()
  const SmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const hasAuthority = useHasAuthority()
  const classes = useStyles()
  const [createOpen, setCreateOpen] = useState<boolean>(false)

  const handleOpenCreate = () => {
    setCreateOpen(true)
  }

  const handleCloseCreate = () => {
    setCreateOpen(false)
  }

  if (!hasAuthority(Authority.VIEW_PRINT_ITEM_PARAMETERS)) {
    return null
  }

  return (
    <ResourceContextProvider value={ResourceName.PRINT_ITEM_PARAMETERS}>
      <PrintItemParametersCreateDialog
        {...props}
        basePath={`/${ResourceName.PRINT_ITEM_PARAMETERS}`}
        handleClose={handleCloseCreate}
        resource={ResourceName.PRINT_ITEM_PARAMETERS}
        open={createOpen}
        itemId={itemId}
      />
      <List
        {...props}
        exporter={undefined}
        resource={ResourceName.PRINT_ITEM_PARAMETERS}
        basePath={`/${ResourceName.PRINT_ITEM_PARAMETERS}`}
        filters={undefined}
        filter={{ printItemId: itemId }}
        bulkActionButtons={false}
        perPage={20}
        pagination={false}
        empty={
          <ItemParametersTopToolbarActions
            itemId={itemId}
            handleOpenCreate={handleOpenCreate}
          />
        }
        title=" "
        actions={false}
        className={classes.listMarginCorrection}
      >
        <EditableDatagrid
          {...props}
          mutationMode="pessimistic"
          undoable={false}
          padding={SmallScreen ? 'checkbox' : 'default'}
          editForm={<ItemParameterEdit />}
          createForm={undefined}
          hasBulkActions={false}
          defaultTitle={null}
          title=" "
          handleDelete={undefined}
          actions={
            <ActionsColumn
              label={<InlineCreateButton onClick={handleOpenCreate} />}
              mutationMode="pessimistic"
              redirect={false}
            />
          }
        >
          <TextField label="ID" source="id" sortable={false} />
          <TextField
            resource={ResourceName.PRINT_ITEM_PARAMETERS}
            source="key"
            sortable={false}
          />
          <TextField
            resource={ResourceName.PRINT_ITEM_PARAMETERS}
            source="options"
            sortable={false}
          />
          <TextField
            resource={ResourceName.PRINT_ITEM_PARAMETERS}
            source="type"
            sortable={false}
          />
        </EditableDatagrid>
      </List>
    </ResourceContextProvider>
  )
}
